import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { motion } from 'framer-motion';
import {
    FaDownload, FaSave, FaFilePdf, FaEdit, FaCheckCircle,
    FaUndo, FaEye, FaInfoCircle,
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import { generateBrochurePDF } from '../utils/brochureGenerator';
import { safeFetch } from '../utils/apiUtils';
import './AdminBrochure.css';

const API = 'https://aayushrealproperties.in/nimaibackend/public/api';

const DEFAULT_CONFIG = {
    tagline:   'Premium Web & Mobile App Development Studio in Vadodara',
    aboutText: 'Nimai Solutions is a boutique digital agency based in Vadodara, Gujarat. We craft high-performance web applications, cross-platform mobile apps, and purposeful UI/UX designs that help businesses grow in the digital era.',
    mission:   'Empower businesses with scalable, elegant digital products that create lasting impact.',
    vision:    'To be the most trusted and innovative software development studio in India by 2030.',
    values:    'Integrity · Transparency · Scalable Engineering · Human-Centric Design · Continuous Learning',
    phone:     '+91 99999 00000',
    email:     'hello@nimaisolutions.com',
    website:   'https://nimaisolutions.com',
    address:   'Vadodara, Gujarat, India',
};

const FIELD_META = [
    { group: 'Branding',
      fields: [
          { key: 'tagline',   label: 'Company Tagline',        type: 'text',     hint: 'Appears on cover page below the company name' },
      ]
    },
    { group: 'About Section',
      fields: [
          { key: 'aboutText', label: 'About Us Paragraph',     type: 'textarea', hint: 'Main body text on the About page' },
          { key: 'mission',   label: 'Mission Statement',       type: 'textarea', hint: 'Appears in the Mission card' },
          { key: 'vision',    label: 'Vision Statement',        type: 'textarea', hint: 'Appears in the Vision card' },
          { key: 'values',    label: 'Core Values',             type: 'text',     hint: 'Separate values with · (e.g. Integrity · Innovation)' },
      ]
    },
    { group: 'Contact Information',
      fields: [
          { key: 'phone',   label: 'Phone Number', type: 'text', hint: 'Shown on cover & contact pages' },
          { key: 'email',   label: 'Email Address', type: 'text', hint: 'Shown on cover & contact pages' },
          { key: 'website', label: 'Website URL',   type: 'text', hint: 'Shown in footer of each page' },
          { key: 'address', label: 'Office Address', type: 'text', hint: 'City / full address shown on contact page' },
      ]
    },
];

const AdminBrochure = () => {
    const [config, setConfig]       = useState({ ...DEFAULT_CONFIG });
    const [original, setOriginal]   = useState({ ...DEFAULT_CONFIG });
    const [services, setServices]   = useState([]);
    const [stats,    setStats]      = useState([]);
    const [isSaving, setIsSaving]   = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [downloaded, setDownloaded]     = useState(false);
    const [hasChanges, setHasChanges]     = useState(false);

    /* ── Load data ─────────────────────────────────────────── */
    useEffect(() => {
        const load = async () => {
            try {
                const token = localStorage.getItem('admin_token');
                const headers = { Accept: 'application/json', Authorization: `Bearer ${token}` };

                const [svcRes, statRes, cfgRes] = await Promise.all([
                    safeFetch(`${API}/services`,          { headers }),
                    safeFetch(`${API}/experience-stats`,  { headers }),
                    safeFetch(`${API}/brochure-config`,   { headers })
                        .catch(() => ({ ok: false, data: null })),
                ]);

                if (svcRes.ok) {
                    const d = svcRes.data?.data || svcRes.data;
                    setServices(Array.isArray(d) ? d : []);
                }
                if (statRes.ok) {
                    const d = statRes.data?.data || statRes.data;
                    setStats(Array.isArray(d) ? d : Object.values(d || {}));
                }
                if (cfgRes.ok && cfgRes.data) {
                    const saved = cfgRes.data?.data || cfgRes.data;
                    if (saved && typeof saved === 'object') {
                        const merged = { ...DEFAULT_CONFIG, ...saved };
                        setConfig(merged);
                        setOriginal(merged);
                    }
                }
            } catch (err) {
                console.error('AdminBrochure load error:', err);
            }
        };
        load();
    }, []);

    /* Track unsaved changes */
    useEffect(() => {
        setHasChanges(JSON.stringify(config) !== JSON.stringify(original));
    }, [config, original]);

    /* ── Handlers ──────────────────────────────────────────── */
    const handleChange = (key, value) => {
        setConfig(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const token = localStorage.getItem('admin_token');
            const { ok } = await safeFetch(`${API}/brochure-config`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(config),
            });

            if (ok) {
                toast.success('Brochure settings saved successfully!');
                setOriginal({ ...config });
                setHasChanges(false);
            } else {
                // Save locally if API not implemented yet
                setOriginal({ ...config });
                setHasChanges(false);
                toast.success('Settings saved locally (backend sync pending)');
            }
        } catch (err) {
            setOriginal({ ...config });
            setHasChanges(false);
            toast.success('Settings saved locally (offline)');
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        setConfig({ ...original });
        toast('Changes reverted', { icon: '↩️' });
    };

    const handleGenerate = async () => {
        if (isGenerating) return;
        setIsGenerating(true);
        try {
            await generateBrochurePDF(config, services, stats);
            setDownloaded(true);
            toast.success('Brochure downloaded!');
            setTimeout(() => setDownloaded(false), 4000);
        } catch (err) {
            console.error('PDF generation error:', err);
            toast.error('Failed to generate PDF. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    /* ── Render ────────────────────────────────────────────── */
    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">

                {/* Header */}
                <header className="content-header">
                    <div>
                        <h1>Brochure <span>Generator</span></h1>
                        <p>Customise the company brochure content and generate a ready-to-download PDF.</p>
                    </div>
                    <div className="brochure-header-actions">
                        {hasChanges && (
                            <motion.button
                                className="btn-ghost"
                                onClick={handleReset}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                title="Revert changes"
                            >
                                <FaUndo /> Revert
                            </motion.button>
                        )}
                        <button
                            className={`btn-save ${isSaving ? 'loading' : ''}`}
                            onClick={handleSave}
                            disabled={isSaving || !hasChanges}
                        >
                            {isSaving ? <><span className="btn-spinner-dark" /> Saving…</> : <><FaSave /> Save Settings</>}
                        </button>
                        <motion.button
                            className={`btn-generate ${isGenerating ? 'loading' : ''} ${downloaded ? 'success' : ''}`}
                            onClick={handleGenerate}
                            whileHover={!isGenerating ? { scale: 1.03 } : {}}
                            whileTap={!isGenerating ? { scale: 0.97 } : {}}
                            disabled={isGenerating}
                        >
                            {isGenerating ? (
                                <><span className="btn-spinner" /> Generating…</>
                            ) : downloaded ? (
                                <><FaCheckCircle /> Downloaded!</>
                            ) : (
                                <><FaDownload /> Generate & Download</>
                            )}
                        </motion.button>
                    </div>
                </header>

                {/* Unsaved changes banner */}
                {hasChanges && (
                    <motion.div
                        className="unsaved-banner"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <FaEdit /> You have unsaved changes. Save settings before generating the final brochure.
                    </motion.div>
                )}

                {/* Data summary cards */}
                <div className="brochure-data-summary">
                    <div className="data-card">
                        <div className="data-card-icon services-icon"><FaFilePdf /></div>
                        <div>
                            <div className="data-card-value">{services.length || 6}</div>
                            <div className="data-card-label">Services in Brochure</div>
                        </div>
                    </div>
                    <div className="data-card">
                        <div className="data-card-icon stats-icon"><FaCheckCircle /></div>
                        <div>
                            <div className="data-card-value">{stats.length || 4}</div>
                            <div className="data-card-label">Stats Included</div>
                        </div>
                    </div>
                    <div className="data-card">
                        <div className="data-card-icon pages-icon"><FaEye /></div>
                        <div>
                            <div className="data-card-value">5</div>
                            <div className="data-card-label">Brochure Pages</div>
                        </div>
                    </div>
                    <div className="data-card info-card">
                        <FaInfoCircle className="info-icon" />
                        <p>Services and Stats are pulled automatically from your dashboard. Only the text content below is editable here.</p>
                    </div>
                </div>

                {/* Form groups */}
                <div className="brochure-form-wrapper">
                    {FIELD_META.map((group) => (
                        <div className="brochure-form-group" key={group.group}>
                            <h3 className="form-group-title">{group.group}</h3>
                            <div className="brochure-fields-grid">
                                {group.fields.map((field) => (
                                    <div
                                        key={field.key}
                                        className={`brochure-field ${field.type === 'textarea' ? 'full-width' : ''}`}
                                    >
                                        <label htmlFor={field.key}>{field.label}</label>
                                        {field.type === 'textarea' ? (
                                            <textarea
                                                id={field.key}
                                                rows={3}
                                                value={config[field.key] || ''}
                                                onChange={(e) => handleChange(field.key, e.target.value)}
                                                placeholder={DEFAULT_CONFIG[field.key]}
                                            />
                                        ) : (
                                            <input
                                                id={field.key}
                                                type="text"
                                                value={config[field.key] || ''}
                                                onChange={(e) => handleChange(field.key, e.target.value)}
                                                placeholder={DEFAULT_CONFIG[field.key]}
                                            />
                                        )}
                                        {field.hint && (
                                            <span className="field-hint">{field.hint}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom generate CTA */}
                <div className="brochure-generate-cta">
                    <div className="cta-left">
                        <FaFilePdf className="cta-pdf-icon" />
                        <div>
                            <h4>Ready to Generate</h4>
                            <p>5-page professional A4 brochure with live services, stats & your custom content.</p>
                        </div>
                    </div>
                    <motion.button
                        className={`btn-generate large ${isGenerating ? 'loading' : ''} ${downloaded ? 'success' : ''}`}
                        onClick={handleGenerate}
                        whileHover={!isGenerating ? { scale: 1.03 } : {}}
                        whileTap={!isGenerating ? { scale: 0.97 } : {}}
                        disabled={isGenerating}
                    >
                        {isGenerating ? (
                            <><span className="btn-spinner" /> Building PDF…</>
                        ) : downloaded ? (
                            <><FaCheckCircle /> PDF Downloaded!</>
                        ) : (
                            <><FaDownload /> Generate & Download Brochure</>
                        )}
                    </motion.button>
                </div>

            </main>
        </div>
    );
};

export default AdminBrochure;
